import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO_PAGES from '../../lib/seo-pages.json';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Logo } from '../Logo';
import { useForcedDarkMode } from '../../contexts/ThemeContext';
import { trackMarketingEvent } from '../../lib/marketingAnalytics';
import { openHashTarget } from './blocks';
import { TRIAL_PATH } from '../../lib/offer';
import './marketing.css';

export const SOLUTIONS = [
  { to: '/solutions/grc', label: 'Governance and compliance', key: 'grc' },
  { to: '/solutions/ma', label: 'M&A', key: 'ma' },
  { to: '/ma-plus', label: 'M&A Pro+', key: 'ma-plus' },
  { to: '/solutions/suppliers', label: 'Procurement and suppliers', key: 'suppliers' },
  { to: '/solutions/contracts', label: 'Contracts', key: 'contracts' },
  { to: '/solutions/pmo', label: 'Projects', key: 'pmo' },
  { to: '/solutions/company-secretary', label: 'Company secretarial', key: 'company-secretary' },
];

const SITE = 'https://simplif-i.com';

const SolutionsMenu = ({ page }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const toggleRef = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => {
      if (e.key !== 'Escape') return;
      if (ref.current && ref.current.contains(document.activeElement)) toggleRef.current?.focus();
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, []);
  return (
    <div className="mk-dd" ref={ref} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
      <button ref={toggleRef} type="button" aria-expanded={open} aria-controls="mk-solutions-menu" onClick={() => setOpen(!open)} data-testid="nav-solutions-toggle">
        Solutions <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <ul id="mk-solutions-menu" className="mk-dd-menu" data-testid="nav-solutions-menu">
          {SOLUTIONS.map((s) => (
            <li key={s.to}>
              <Link to={s.to} data-testid={`nav-solution-${s.key}`} onClick={() => { setOpen(false); trackMarketingEvent('solution_navigation_click', { page, solution: s.key, destination: s.to, section: 'header' }); }}>{s.label}</Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MobileMenu = ({ page, onClose, loggedIn }) => (
  <nav className="mk-mobile mk-container" id="mk-mobile-menu" aria-label="Mobile" data-testid="nav-mobile-menu">
    <Link to="/#capabilities" onClick={onClose}>Platform</Link>
    <p className="mk-mobile-group">Solutions</p>
    {SOLUTIONS.map((s) => (
      <Link key={s.to} to={s.to} data-testid={`nav-mobile-solution-${s.key}`} onClick={() => { onClose(); trackMarketingEvent('solution_navigation_click', { page, solution: s.key, destination: s.to, section: 'mobile-menu' }); }}>{s.label}</Link>
    ))}
    <p className="mk-mobile-group">More</p>
    <Link to="/pricing" onClick={onClose}>Pricing</Link>
    <Link to="/blog" onClick={onClose}>Resources</Link>
    {loggedIn
      ? <Link to="/dashboard" onClick={onClose} data-testid="nav-mobile-dashboard">Go to dashboard</Link>
      : <Link to="/login" onClick={onClose} data-testid="nav-mobile-login">Log in</Link>}
  </nav>
);

const Header = ({ page }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const loggedIn = Boolean(localStorage.getItem('session_token'));
  useEffect(() => setMobileOpen(false), [pathname]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [mobileOpen]);
  return (
    <header className="mk-header" data-testid="marketing-header">
      <div className="mk-container mk-header-inner">
        <Link to="/" className="mk-logo" aria-label="Simplif-i home" data-testid="nav-logo"><Logo className="h-9" textClassName="text-white" /></Link>
        <nav className="mk-nav" aria-label="Main">
          <Link to="/#capabilities" data-testid="nav-platform">Platform</Link>
          <SolutionsMenu page={page} />
          <Link to="/pricing" data-testid="nav-pricing">Pricing</Link>
          <Link to="/blog" data-testid="nav-resources">Resources</Link>
        </nav>
        <div className="mk-head-actions">
          {loggedIn
            ? <Link to="/dashboard" className="mk-btn secondary mk-hide-sm" data-testid="nav-dashboard">Go to dashboard</Link>
            : <Link to="/login" className="mk-btn secondary mk-hide-sm" data-testid="nav-login">Log in</Link>}
          <Link to={TRIAL_PATH} className="mk-btn" data-testid="nav-trial-btn" onClick={() => trackMarketingEvent('marketing_cta_click', { page, section: 'header', label: 'Start free trial', destination: TRIAL_PATH })}>Start free trial</Link>
          <button type="button" className="mk-btn secondary mk-menu-btn" aria-expanded={mobileOpen} aria-controls="mk-mobile-menu" aria-label={mobileOpen ? 'Close menu' : 'Open menu'} onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {mobileOpen && <MobileMenu page={page} loggedIn={loggedIn} onClose={() => setMobileOpen(false)} />}
    </header>
  );
};

const FooterList = ({ title, links }) => (
  <div>
    <h3>{title}</h3>
    <ul>{links.map(([to, label]) => <li key={to}><Link to={to}>{label}</Link></li>)}</ul>
  </div>
);

const Footer = () => (
  <footer className="mk-footer" data-testid="marketing-footer">
    <div className="mk-container">
      <div className="mk-footer-grid">
        <div className="mk-footer-brand">
          <Logo className="h-9" textClassName="text-white" />
          <p className="mk-muted" style={{ marginTop: 16 }}>Connected business management.</p>
          <p className="mk-muted" style={{ fontSize: 14 }}>Simplif-i Systems Ltd, 66 Paul Street, London, EC2A 4NA</p>
          <Link to="/contact" className="mk-link">Contact us</Link>
        </div>
        <FooterList title="Solutions" links={SOLUTIONS.map((s) => [s.to, s.label])} />
        <FooterList title="Company" links={[['/about', 'About'], ['/pricing', 'Pricing'], ['/blog', 'Resources'], ['/contact', 'Contact'], ['/contact?type=demo', 'Book a demo'], ['/privacy', 'Privacy Policy'], ['/terms', 'Terms of Service']]} />
        <FooterList title="Compare" links={[['/vs/smartsuite', 'Simplif-i vs SmartSuite'], ['/vs/diligent', 'Simplif-i vs Diligent']]} />
      </div>
      <div className="mk-footer-base">
        <span>&copy; {new Date().getFullYear()} Simplif-i Systems Ltd. All rights reserved.</span>
        <button type="button" className="mk-link" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit' }} onClick={() => window.dispatchEvent(new Event('simplifi:open-cookie-settings'))} data-testid="footer-cookie-settings">Cookie settings</button>
      </div>
    </div>
  </footer>
);

const useHashScroll = () => {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const t = setTimeout(() => openHashTarget(hash.slice(1)), 60);
    return () => clearTimeout(t);
  }, [hash, pathname]);
};

const useDarkAfterThemeProvider = () => {
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      document.documentElement.classList.remove('light');
      document.documentElement.classList.add('dark');
    });
    return () => cancelAnimationFrame(id);
  }, []);
};

export const MarketingLayout = ({ page, path, title: pageTitle, description: pageDescription, children }) => {
  const title = SEO_PAGES[path]?.title || pageTitle;
  const description = SEO_PAGES[path]?.description || pageDescription;
  useForcedDarkMode();
  useDarkAfterThemeProvider();
  useHashScroll();
  const url = `${SITE}${path}`;
  return (
    <div className="mk" data-testid={`marketing-page-${page}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            { '@type': 'Organization', name: 'Simplif-i Systems Ltd', url: SITE, logo: `${SITE}/logo.webp`, address: { '@type': 'PostalAddress', streetAddress: '66 Paul Street', addressLocality: 'London', postalCode: 'EC2A 4NA', addressCountry: 'GB' } },
            { '@type': 'SoftwareApplication', name: 'Simplif-i', applicationCategory: 'BusinessApplication', operatingSystem: 'Web', url },
          ],
        })}</script>
      </Helmet>
      <a href="#main" className="sr-only focus:not-sr-only">Skip to content</a>
      <Header page={page} />
      <main id="main" tabIndex={-1} style={{ outline: 'none' }}>{children}</main>
      <Footer />
    </div>
  );
};

// Shared header/footer for pages that keep their own SEO tags and content styling (blog, tools)
export const MarketingHeader = ({ page }) => {
  useForcedDarkMode();
  useDarkAfterThemeProvider();
  return (
    <div className="mk" style={{ minHeight: 0, position: 'sticky', top: 0, zIndex: 50 }}>
      <Header page={page} />
    </div>
  );
};

export const MarketingFooter = () => <div className="mk" style={{ minHeight: 0 }}><Footer /></div>;

export default MarketingLayout;
